<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="SMS and WhatsApp reminders for high-intent client sessions"
            subtitle="Reach clients in the channels they check first and reduce no-shows for discovery calls, paid consultations, and recurring sessions."
            buttonLabel="Join reminder early access"
            :buttonUrl="heroRegisterUrl"
        >
            <template #afterSubtitle>
                <div
                    class="mt-6 mx-auto max-w-fit rounded-xl border border-amber-200 bg-amber-50 py-4 px-6 text-sm text-amber-900"
                >
                    <div>
                        This is part of our upcoming
                        <a
                            :href="automationsPageUrl"
                            class="font-bold underline underline-offset-2 hover:opacity-90"
                        >
                            Automations feature
                        </a>
                        rollout.
                    </div>
                </div>
            </template>
        </FeaturePageHeroSection>

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div class="order-1">
                        <FeaturePageSectionHeader
                            title="Built for no-show reduction"
                            subtitle="Control message content, delivery coverage, and credit usage without stitching together another reminder tool."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <FeaturePagePrimaryButton
                            class="mt-7"
                            label="Join reminder early access"
                            :href="ctaRegisterUrl"
                        />
                    </div>

                    <div class="order-2">
                        <FeaturePageMockPanel
                            title="Reminder Automation Policy"
                            badgeText="Upcoming"
                        >
                            <FeaturePageInfoCard
                                title="Message templates"
                                description="Customize SMS and WhatsApp copy with merge tags."
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Credit policy"
                                description="Monthly plan credits reset each cycle. Purchased add-on credits do not expire."
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Country targeting"
                                description="Choose countries where messaging is enabled to control delivery costs."
                                containerClass="rounded-xl border border-slate-200 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Reminder capability breakdown"
                    subtitle="Design reminder flows with the controls you need for tone, delivery, and credit strategy."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="reminderFeature in reminderFeatures"
                        :key="reminderFeature.title"
                        :title="reminderFeature.title"
                        :description="reminderFeature.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    />
                </div>
            </div>
        </section>

        <section class="bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Reminder controls matter most when a missed session means lost revenue, wasted prep time, or broken client momentum."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import { RoutePathMarketingEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface ReminderFeature {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.SmsWhatsAppRemindersHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.SmsWhatsAppRemindersCta,
    )
    const automationsPageUrl = urlFactory.makeMarketingRoutePageUrl(
        RoutePathMarketingEnum.Feature_Automations,
    )

    const reminderFeatures: ReminderFeature[] = [
        {
            title: 'Editable message content',
            description:
                'Customize SMS and WhatsApp reminder text to match your brand, offer, and tone.',
        },
        {
            title: 'Merge tags included',
            description:
                'Use variables like client name, meeting date, and meeting time inside reminder messages.',
        },
        {
            title: 'Monthly plan credits',
            description:
                'Every pricing plan includes a monthly pool of reminder credits that reset each month.',
        },
        {
            title: 'Non-expiring add-on credits',
            description:
                'Buy extra credits when needed, and those purchased credits do not expire.',
        },
        {
            title: 'Country-based delivery controls',
            description:
                'Because SMS and WhatsApp pricing varies by country, you can choose where to send messages.',
        },
        {
            title: 'Fewer no-shows',
            description:
                'SMS and WhatsApp reminders are typically seen faster than email, helping reduce missed meetings.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Cross-border coaching',
            description:
                'Enable reminders only for selected countries to keep messaging spend aligned with client regions.',
        },
        {
            title: 'Paid strategy sessions',
            description:
                'Use branded reminder copy with merge tags to reduce missed high-value consulting calls.',
        },
        {
            title: 'Recurring accountability sessions',
            description:
                'Use higher-visibility reminders to keep weekly coaching or advisory sessions consistent.',
        },
    ]
</script>
